// İSG uzmanı portalı — Belge Şemaları: platform belge türü kaydının yönetimi (GL #7 contract
// §4.1.2-§4.1.4). Kayıt TÜM firmalarda ortaktır; her yükleme seçicisi, etiket ve geçerlilik kuralı
// buradan gelir. Yapay zekâ tanıması buradan değiştirilmez (doc-check sürümlerinde yaşar, §20.7);
// liste yalnız canlı sürümün türü tanıyıp tanımadığını gösterir.
//
// Yedi sütun (tasarım review §2.3): kod etiketin altında, etiket hücresi kırpılmaz. Oluştur/düzenle
// tek bir modaldır (`DocumentTypeFormModal`); pasifleştirme yalnız formun içindedir (⋮ sütunu yok,
// tek yol). Liste kapısını geçen herkes "Yeni belge türü"nü ve düzenleme düğmesini görür (İş İzni
// Tipleri deseni); yazma yetkisi olmayanın kaydı 403 ile formun uyarısına düşer.

import {
  Alert,
  Breadcrumb,
  Button,
  Card,
  CardContent,
  DOCUMENT_ENTITIES,
  type DocumentTypeAdminDto,
  type DocumentTypeAdminListParams,
  EmptyState,
  PageHeader,
  Pagination,
  SelectMenu,
  type SelectOption,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableMessageRow,
  TableRow,
  TableSkeletonRows,
  TableToolbar,
  TableToolbarFilter,
  Tag,
  documentSchemasApi,
  documentTypeLabel,
  resolveApiErrorMessage,
  useRowRemovalFocus,
} from '@isgportal/shared';
import { useQuery } from '@tanstack/react-query';
import { FileText, Plus, SearchX } from 'lucide-react';
import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RecognitionMark } from '../../components/documents/RecognitionMark';
import {
  DocumentTypeFormModal,
  type DocumentTypeFormTarget,
} from '../../components/schemas/DocumentTypeFormModal';
import { activeTagColor } from '../../components/status/statusTagColors';
import { schemasKeys } from '../../lib/schemasKeys';
import { useTableDensity } from '../../lib/uiPrefs';
import { useDebouncedValue } from '../../lib/useDebouncedValue';

// Gerçek sunucu sayfalaması (§3 `page`/`pageSize`, `filteredTotal`), diğer liste ekranlarıyla aynı.
const PAGE_SIZE = 20;
const COLUMNS = 7;
const NO_ROWS: DocumentTypeAdminDto[] = [];

/** Durum filtresi → `isActive` param (undefined = hepsi). */
function activeParam(value: string): boolean | undefined {
  if (value === 'active') {
    return true;
  }
  if (value === 'inactive') {
    return false;
  }
  return undefined;
}

export function DocumentTypesListPage() {
  const { t, i18n } = useTranslation();
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search);
  const [entityFilter, setEntityFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [message, setMessage] = useState<string | null>(null);
  const [formTarget, setFormTarget] = useState<DocumentTypeFormTarget | null>(null);
  const addButtonRef = useRef<HTMLButtonElement>(null);
  const [density, setDensity] = useTableDensity();

  const entity = DOCUMENT_ENTITIES.find((value) => value === entityFilter);
  const params: DocumentTypeAdminListParams = {
    search: debouncedSearch,
    entity,
    isActive: activeParam(statusFilter),
    page,
    pageSize: PAGE_SIZE,
  };
  const query = useQuery({
    queryKey: schemasKeys.typeList(params),
    queryFn: () => documentSchemasApi.listDocumentTypes(params),
    placeholderData: (prev) => prev,
  });
  const rows = query.data?.items ?? NO_ROWS;
  const rowKeys = useMemo(() => rows.map((row) => row.uuid), [rows]);

  // Kaydedilen satır süzgeçten düşerse (ör. "Aktif" süzgecinde pasifleştirildi) formun kapanış dönüşü
  // kaybolan etiket düğmesine gidemez: liste tazelenince odak "Yeni belge türü"ne iner.
  const rowFocus = useRowRemovalFocus({
    rowKeys,
    stamps: { dataUpdatedAt: query.dataUpdatedAt, errorUpdatedAt: query.errorUpdatedAt },
    addButtonRef,
    dialogOpen: formTarget !== null,
    otherSurfaceOpen: false,
  });

  const entityOptions: SelectOption[] = useMemo(
    () => [
      { value: '', label: t('documentSchemas.types.filter.allEntities') },
      ...DOCUMENT_ENTITIES.map((value) => ({
        value,
        label: t(`documentSchemas.entity.${value}`),
      })),
    ],
    [t],
  );
  const statusOptions: SelectOption[] = useMemo(
    () => [
      { value: '', label: t('documentSchemas.types.filter.allStatuses') },
      { value: 'active', label: t('documentSchemas.type.status.active') },
      { value: 'inactive', label: t('documentSchemas.type.status.inactive') },
    ],
    [t],
  );

  const hasActiveFilters = Boolean(debouncedSearch || entityFilter || statusFilter);
  // Yapay zekâ durumu tek bir katalog okumasından gelir: `Unknown` küreseldir, tek uyarı yeter.
  const recognitionUnknown = rows.some((row) => row.recognitionStatus === 'Unknown');

  function resetPage<T>(setter: (value: T) => void) {
    return (value: T) => {
      setter(value);
      setPage(1);
    };
  }

  function clearFilters() {
    setSearch('');
    setEntityFilter('');
    setStatusFilter('');
    setPage(1);
  }

  function openCreate() {
    setMessage(null);
    setFormTarget({ mode: 'create', entity: entity ?? null });
  }

  function openEdit(row: DocumentTypeAdminDto) {
    setMessage(null);
    setFormTarget({ mode: 'edit', row });
  }

  function handleSaved(saved: DocumentTypeAdminDto) {
    if (formTarget?.mode === 'edit') {
      setMessage(t('documentSchemas.types.updated'));
      // Satırı TUTAN ama süzgeçten düşürebilen kayıt: yalnız kur — satır yenilemede kalkarsa odak
      // "Yeni belge türü"ne iner, kalırsa formun dönüşü etiket düğmesinde kalır.
      rowFocus.removeFailed(saved.uuid);
    } else {
      setMessage(t('documentSchemas.types.created'));
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <Breadcrumb items={[{ label: t('documentSchemas.title') }]} />
      <PageHeader
        title={t('documentSchemas.title')}
        helperText={t('documentSchemas.types.subtitle')}
        actions={
          <Button ref={addButtonRef} onClick={openCreate}>
            <Plus className="h-4 w-4" aria-hidden="true" />
            {t('documentSchemas.types.new')}
          </Button>
        }
      />

      {message ? <Alert tone="success">{message}</Alert> : null}
      {recognitionUnknown ? (
        <Alert tone="info">{t('documentSchemas.types.aiUnknown')}</Alert>
      ) : null}

      <Card className="carbon:p-0">
        <CardContent className="flex flex-col gap-3 carbon:gap-0 carbon:p-0">
          <TableToolbar
            summary={
              query.data
                ? t('documentSchemas.types.count', { count: query.data.filteredTotal })
                : undefined
            }
            search={search}
            onSearchChange={resetPage(setSearch)}
            searchLabel={t('documentSchemas.common.search')}
            searchPlaceholder={t('documentSchemas.types.searchPlaceholder')}
            density={density}
            onDensityChange={setDensity}
            densityLabel={t('common.compactRows')}
            filters={
              <>
                <TableToolbarFilter>
                  <SelectMenu
                    ariaLabel={t('documentSchemas.types.filter.entity')}
                    placeholder={t('documentSchemas.types.filter.allEntities')}
                    options={entityOptions}
                    value={entityFilter}
                    onValueChange={resetPage(setEntityFilter)}
                  />
                </TableToolbarFilter>
                <TableToolbarFilter>
                  <SelectMenu
                    ariaLabel={t('documentSchemas.types.filter.status')}
                    placeholder={t('documentSchemas.types.filter.allStatuses')}
                    options={statusOptions}
                    value={statusFilter}
                    onValueChange={resetPage(setStatusFilter)}
                  />
                </TableToolbarFilter>
              </>
            }
          />

          <Table caption={t('documentSchemas.tab.types')} density={density}>
            <TableHeader>
              <TableRow>
                <TableHead>{t('documentSchemas.types.col.label')}</TableHead>
                <TableHead>{t('documentSchemas.types.col.entity')}</TableHead>
                <TableHead className="w-16 text-right">
                  {t('documentSchemas.types.col.sortOrder')}
                </TableHead>
                <TableHead>{t('documentSchemas.types.col.expiryRule')}</TableHead>
                <TableHead>{t('documentSchemas.types.col.captureFormat')}</TableHead>
                <TableHead>{t('documentSchemas.types.col.ai')}</TableHead>
                <TableHead>{t('documentSchemas.types.col.status')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody aria-busy={query.isPending || undefined}>
              {query.isPending ? (
                <TableSkeletonRows columns={COLUMNS} />
              ) : query.isError ? (
                <TableMessageRow colSpan={COLUMNS}>
                  {resolveApiErrorMessage(query.error, t)}
                </TableMessageRow>
              ) : rows.length === 0 ? (
                <TableMessageRow colSpan={COLUMNS}>
                  {hasActiveFilters ? (
                    <EmptyState
                      icon={SearchX}
                      title={t('documentSchemas.types.noResults')}
                      description={t('common.noResults.body')}
                      action={
                        <Button variant="ghost" size="sm" onClick={clearFilters}>
                          {t('common.clearFilters')}
                        </Button>
                      }
                      role="status"
                    />
                  ) : (
                    <EmptyState
                      icon={FileText}
                      title={t('documentSchemas.types.empty')}
                      action={
                        <Button variant="ghost" size="sm" onClick={openCreate}>
                          <Plus className="h-4 w-4" aria-hidden="true" />
                          {t('documentSchemas.types.new')}
                        </Button>
                      }
                    />
                  )}
                </TableMessageRow>
              ) : (
                rows.map((row) => (
                  <TableRow key={row.uuid} className="carbon:h-auto carbon:min-h-[var(--row-h)]">
                    <TableCell truncate={false} className="min-w-0 carbon:py-2">
                      {/* Etiket yönetilen şeyin kendisidir: kırpılmaz (160 karaktere kadar). Birincil
                          "aç" = etiket düğmesi (İş İzni Tipleri deseni) → düzenleme modalı. */}
                      <div className="flex min-w-0 flex-col gap-0.5 break-words">
                        <button
                          type="button"
                          onClick={() => openEdit(row)}
                          className="block text-left font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        >
                          {documentTypeLabel([row], row.entity, row.code, i18n.language)}
                        </button>
                        <code className="block break-all text-xs text-muted">{row.code}</code>
                      </div>
                    </TableCell>
                    <TableCell truncate={false} className="text-muted carbon:py-2">
                      {t(`documentSchemas.entity.${row.entity}`)}
                    </TableCell>
                    <TableCell className="w-16 text-right text-muted tnum">
                      {row.sortOrder}
                    </TableCell>
                    <TableCell truncate={false} className="text-muted carbon:py-2">
                      {t(`documentSchemas.expiryRule.${row.expiryRule}`)}
                    </TableCell>
                    <TableCell className="text-muted">
                      {t(`documentSchemas.captureFormat.${row.captureFormat}`)}
                    </TableCell>
                    <TableCell>
                      <RecognitionCell row={row} />
                    </TableCell>
                    <TableCell>
                      <Tag color={activeTagColor(row.isActive)}>
                        {t(
                          row.isActive
                            ? 'documentSchemas.type.status.active'
                            : 'documentSchemas.type.status.inactive',
                        )}
                      </Tag>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>

          {query.data ? (
            <Pagination
              page={page}
              pageSize={PAGE_SIZE}
              filteredTotal={query.data.filteredTotal}
              onPageChange={setPage}
              labels={{
                nav: t('documentSchemas.types.pagination.nav'),
                previous: t('documentSchemas.types.pagination.previous'),
                next: t('documentSchemas.types.pagination.next'),
              }}
            />
          ) : null}
        </CardContent>
      </Card>

      {formTarget ? (
        <DocumentTypeFormModal
          key={formTarget.mode === 'edit' ? formTarget.row.uuid : 'create'}
          target={formTarget}
          // Oluşturma: dönüş kalıcı "Yeni belge türü"ne (boş durumun düğmesi liste dolunca kalkar).
          // Düzenleme: Modal açılışta yakaladığı etiket düğmesine döner.
          restoreFocusRef={formTarget.mode === 'create' ? addButtonRef : undefined}
          onClose={() => setFormTarget(null)}
          onSaved={handleSaved}
          onRowStale={(uuid) => rowFocus.removeFailed(uuid)}
        />
      ) : null}
    </div>
  );
}

/** "AI tanıma" hücresi: hap değil, sessiz metin + simge (`RecognitionMark`, review §2.3). */
function RecognitionCell({ row }: { row: DocumentTypeAdminDto }) {
  const { t } = useTranslation();
  if (row.recognitionStatus === 'Recognised' && row.recognitionRelease !== null) {
    return <RecognitionMark release={row.recognitionRelease} srText={null} />;
  }
  if (row.recognitionStatus === 'ManualOnly') {
    return (
      <RecognitionMark release={null} noneText={t('documentSchemas.ai.manualOnly')} srText={null} />
    );
  }
  // `Unknown` (ve sözleşme dışı `Recognised` + `null` sürüm): veri değil, okunamadı.
  return <RecognitionMark release={null} noneText="—" srText={t('documentSchemas.ai.unknownSr')} />;
}
